import { Stack, Tile, Typography, createLucideIcon, useTheme } from "@carrotfertility/carotene-core"
import { Skeleton } from "@internal/ui"
import { Calendar } from "lucide-react"
import React from "react"
import { useIntl } from "react-intl"
import { AppointmentsAvatar } from "./AppointmentsAvatar"
import {
  formatHomeAppointmentDate,
  formatHomeAppointmentTime,
  formatProviderName,
  formatStartingIn,
  isSameLocalDay
} from "./formatting"
import { useAppointments } from "./useAppointments"
import { useAppointmentTiming } from "./useAppointmentTiming"
import type { UpcomingAppointment } from "./types"

const CalendarIcon = createLucideIcon(Calendar, "CalendarIcon")

function TileShell({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Tile variant="secondary" color="primary">
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>{children}</Stack>
    </Tile>
  )
}

function Heading(): JSX.Element {
  return (
    <Typography variant="h5" component="h2" color="text.primary">
      Preparing for
    </Typography>
  )
}

function UpcomingBody({ appointment }: { appointment: UpcomingAppointment }): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const { now, imminent } = useAppointmentTiming(appointment)

  const { provider, date } = appointment
  // A wider window than imminence: a 5pm visit reads "Today" all morning without yet offering Join.
  const dateLabel = isSameLocalDay(date, now) ? "Today" : formatHomeAppointmentDate(intl, date)

  return (
    <>
      <Heading />
      <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.sm)}>
        <AppointmentsAvatar name={provider.name} photoUrl={provider.photoUrl} />
        <Typography variant="h6" component="p" color="text.primary">
          {formatProviderName(provider)}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <CalendarIcon fontSize="small" aria-hidden />
        <Typography variant="body2">{`${dateLabel} · ${formatHomeAppointmentTime(intl, date)}`}</Typography>
      </Stack>
      {imminent && (
        // `color` after `typography`, which spreads a role carrying its own.
        <Typography sx={{ typography: "chip", color: theme.palette.primary.main }}>
          {`Starting ${formatStartingIn(intl, date, now)}`}
        </Typography>
      )}
    </>
  )
}

function TileSkeleton(): JSX.Element {
  const theme = useTheme()
  return (
    <TileShell>
      <Stack role="status" aria-label="Loading your appointments" spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Skeleton width={128} height={24} />
        <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.sm)}>
          <Skeleton width={54} height={54} />
          <Skeleton width={120} height={18} />
        </Stack>
        <Skeleton width="80%" height={14} />
        <Skeleton width={140} height={36} sx={{ borderRadius: theme.tokens.borderRadius.md }} />
      </Stack>
    </TileShell>
  )
}

export function SidebarAppointmentsTile(): JSX.Element | null {
  const { data, isLoading, isError } = useAppointments()

  if (isError) {
    return null
  }
  if (isLoading || !data) {
    return <TileSkeleton />
  }

  if (!data.upcoming || data.upcoming.status !== "scheduled") {
    return null
  }

  return (
    <TileShell>
      <UpcomingBody appointment={data.upcoming} />
    </TileShell>
  )
}
